import { Badge, Button, Group, Modal, Paper, SegmentedControl, Stack, Text, TextInput, Textarea } from "@mantine/core"
import { useEffect, useState } from "react"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { QuestionDetails } from "atlas_forecasting_app/components/common/question_details"
import { ErrorAlert, LoadingState, PageHeader, SectionCard } from "atlas_forecasting_app/components/common/page_elements"

export function NewQuestionPage({ onQuestionCreated, draftId = null }) {
  const [draft, setDraft] = useState(null)
  const [input, setInput] = useState("")
  const [sending, setSending] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState("")
  const [importOpen, setImportOpen] = useState(false)
  const [importSource, setImportSource] = useState("metaculus")
  const [metaculusPostId, setMetaculusPostId] = useState("")
  const [kalshiEventReference, setKalshiEventReference] = useState("")
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState("")

  const questionSpec = draft?.question_spec || null
  const hasQuestionSpec = questionSpec && typeof questionSpec === "object" && Object.keys(questionSpec).length > 0
  const isReviewing = !!draft && (draft.state === "reviewing" || draft.state === "failed") && hasQuestionSpec
  const canConfirm = isReviewing && !confirming

  useEffect(() => {
    let mounted = true
    setDraft(null)
    setInput("")
    setError("")

    const path = draftId ? `/api/question_drafts/${draftId}` : "/api/question_drafts"
    const options = draftId ? undefined : { method: "POST", body: {} }

    apiFetch(path, options)
      .then((data) => mounted && setDraft(data.draft))
      .catch((e) => mounted && setError(e.message))

    return () => {
      mounted = false
    }
  }, [draftId])

  const send = async () => {
    if (!draft || !input.trim()) return
    setSending(true)
    try {
      const data = await apiFetch(`/api/question_drafts/${draft.id}/messages`, {
        method: "POST",
        body: { content: input.trim() },
      })
      setDraft(data.draft)
      setInput("")
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setSending(false)
    }
  }

  const confirm = async () => {
    if (!draft || !canConfirm) return
    setConfirming(true)
    try {
      const data = await apiFetch(`/api/question_drafts/${draft.id}/confirm`, { method: "POST", body: {} })
      setDraft(data.draft)
      setError("")
      if (data.question?.id) onQuestionCreated(data.question.id)
      if (data.draft?.created_question_id) onQuestionCreated(data.draft.created_question_id)
    } catch (e) {
      setError(e.message)
    } finally {
      setConfirming(false)
    }
  }

  const closeImportModal = (force = false) => {
    if (!force && importing) return
    setImportOpen(false)
  }

  const importMetaculusQuestion = async () => {
    const raw = metaculusPostId.trim()
    if (!raw.match(/^\d+$/)) {
      setImportError("Enter a valid numeric Metaculus post ID.")
      return
    }

    setImporting(true)
    try {
      const data = await apiFetch("/api/questions/import_metaculus", { method: "POST", body: { post_id: raw } })
      const id = data?.question?.id
      if (!id) throw new Error("Import succeeded but no question ID was returned.")
      setImportError("")
      closeImportModal(true)
      onQuestionCreated(id)
    } catch (e) {
      setImportError(e.message)
    } finally {
      setImporting(false)
    }
  }

  const importKalshiQuestion = async () => {
    const raw = kalshiEventReference.trim()
    if (!raw) {
      setImportError("Enter a Kalshi event ticker or a URL containing one.")
      return
    }

    setImporting(true)
    try {
      const data = await apiFetch("/api/questions/import_kalshi", { method: "POST", body: { event_reference: raw } })
      const id = data?.question?.id
      if (!id) throw new Error("Import succeeded but no question ID was returned.")
      setImportError("")
      closeImportModal(true)
      onQuestionCreated(id)
    } catch (e) {
      setImportError(e.message)
    } finally {
      setImporting(false)
    }
  }

  const placeholder = isReviewing ? "Tell the AI what to change..." : "Type your answer..."

  const pageTitle = draftId ? "Continue draft" : "New question"
  const pageSubtitle = draftId
    ? "Pick up this draft where you left off, refine the spec, and confirm when it’s ready."
    : "Chat to clarify, then review and iterate on the generated question until you confirm."

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow="Create"
        variant="form"
        title={pageTitle}
        subtitle={pageSubtitle}
        action={
          <Button
            type="button"
            variant="light"
            onClick={() => {
              setImportError("")
              setImportSource("metaculus")
              setImportOpen(true)
            }}
          >
            Import from Kalshi or Metaculus
          </Button>
        }
      />

      <ErrorAlert message={error} />

      {hasQuestionSpec ? (
        <SectionCard
          variant="form"
          title="Draft question spec"
          subtitle={
            draft?.state === "reviewing" || (draft?.state === "failed" && hasQuestionSpec)
              ? "Review this draft and request changes, then confirm when it looks right."
              : "The AI will generate a draft spec here once it’s ready."
          }
          action={<Badge variant="light">{questionSpec.type || "draft"}</Badge>}
        >
          <QuestionDetails question={questionSpec} />

          {isReviewing ? (
            <Group gap="sm" align="center">
              <Button onClick={confirm} disabled={!canConfirm}>
                {confirming ? "Confirming..." : "Confirm question"}
              </Button>
              <Text size="sm" c="dimmed">
                After confirming, you can forecast it from the question page.
              </Text>
            </Group>
          ) : null}
        </SectionCard>
      ) : null}

      <SectionCard
        variant="feature"
        title="Draft conversation"
        subtitle={isReviewing ? "Give feedback to refine the draft spec above, then click Confirm when it’s correct." : "Answer the AI’s clarifying questions to reach a well-specified forecast question."}
      >
        {!draft ? (
          <LoadingState label="Starting..." />
        ) : (
          <Stack gap="sm" className="af-draft-thread">
            {(draft.messages || []).map((message, idx) => {
              const role = message.role || "assistant"
              const userMessage = role === "user"
              return (
                <Paper key={idx} withBorder radius="xl" p="md" bg={userMessage ? "blue.0" : "gray.0"} className={`af-draft-thread__message ${userMessage ? "is-user" : "is-assistant"}`}>
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      {role}
                    </Text>
                    <Text style={{ whiteSpace: "pre-wrap" }}>{message.content}</Text>
                  </Stack>
                </Paper>
              )
            })}
          </Stack>
        )}

        <Group align="flex-end" gap="sm" className="af-draft-thread__composer">
          <Textarea
            autosize
            minRows={2}
            maxRows={8}
            style={{ flex: 1 }}
            placeholder={placeholder}
            value={input}
            disabled={sending || !draft}
            onChange={(event) => setInput(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return

              event.preventDefault()
              send()
            }}
          />
          <Button disabled={sending || !draft || !input.trim()} onClick={send}>
            {sending ? "Sending..." : "Send"}
          </Button>
        </Group>
      </SectionCard>

      <Modal opened={importOpen} onClose={closeImportModal} title="Import question">
        <Stack gap="md">
          <SegmentedControl
            fullWidth
            value={importSource}
            onChange={setImportSource}
            data={[
              { label: "Metaculus", value: "metaculus" },
              { label: "Kalshi", value: "kalshi" },
            ]}
            disabled={importing}
          />
          <ErrorAlert message={importError} />
          {importSource === "metaculus" ? (
            <>
              <Text c="dimmed" size="sm">
                Enter the numeric ID of the Metaculus post you want to import.
              </Text>
              <Group align="flex-end" gap="sm">
                <TextInput
                  style={{ flex: 1 }}
                  placeholder="Metaculus post ID (e.g. 12345)"
                  value={metaculusPostId}
                  disabled={importing}
                  onChange={(event) => setMetaculusPostId(event.currentTarget.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      importMetaculusQuestion()
                    }
                  }}
                />
                <Button type="button" disabled={importing || !metaculusPostId.trim()} onClick={importMetaculusQuestion}>
                  {importing ? "Importing..." : "Import"}
                </Button>
              </Group>
            </>
          ) : (
            <>
              <Text c="dimmed" size="sm">
                Enter a Kalshi event ticker like <Text span inherit fw={600}>KXPRESPERSON-28</Text> or paste the URL for an event on Kalshi.
              </Text>
              <Group align="flex-end" gap="sm">
                <TextInput
                  style={{ flex: 1 }}
                  label="Event ticker or URL"
                  placeholder="Event ticker or URL (e.g. KXPRESPERSON-28)"
                  value={kalshiEventReference}
                  disabled={importing}
                  onChange={(event) => setKalshiEventReference(event.currentTarget.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      importKalshiQuestion()
                    }
                  }}
                />
                <Button type="button" disabled={importing || !kalshiEventReference.trim()} onClick={importKalshiQuestion}>
                  {importing ? "Importing..." : "Import"}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </Stack>
  )
}
